import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ActivitySection } from '@/components/pools/details/ActivitySection';
import { Breadcrumb } from '@/components/pools/details/Breadcrumb';
import { ActionType, ConfirmActionModal } from '@/components/pools/details/ConfirmActionModal';
import { CounterOfferModal } from '@/components/pools/details/CounterOfferModal';
import { InfoCard } from '@/components/pools/details/InfoCard';
import { PoolBorrowerActions } from '@/components/pools/details/PoolBorrowerActions';
import { PoolDetailTabs } from '@/components/pools/details/PoolDetailTabs';
import { PoolDocumentsContent } from '@/components/pools/details/PoolDocumentsSection';
import { PoolInvestorActions } from '@/components/pools/details/PoolInvestorActions';
import { PoolOverviewContent } from '@/components/pools/details/PoolOverviewSection';
import { SendOfferModal } from '@/components/pools/details/SendOfferModal';
import { SlideContentWrapper } from '@/components/pools/details/SlideContentWrapper';
import PoolStatusPill from '@/components/pools/PoolStatusPill';
import { useMarkPoolNotificationsReadOnVisit, useNotificationsByPoolId } from '@/hooks/queries/useNotificationQueries';
import { usePool } from '@/hooks/queries/usePoolQueries';
import { useUser } from '@/stores/authStore';
import { getAvailableCounterOffers, hasCounterOffer } from '@/utils/poolStatus';
import { isBorrower, isInvestor } from '@/utils/user';

export default function PoolDetailPage() {
  const params = useParams();
  const poolId = params?.poolId as string;
  const user = useUser();
  const navigate = useNavigate();

  // Scroll to top when poolId changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [poolId]);

  // UI state
  const [activeTab, setActiveTab] = useState<'overview' | 'documents'>('overview');
  const [confirmAction, setConfirmAction] = useState<{ offerId?: number; type: ActionType; isOpen: boolean }>({
    type: 'closePool',
    isOpen: false,
  });
  const [showSendOfferModal, setShowSendOfferModal] = useState(false);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [availableCounterOfferIndex, setAvailableCounterOfferIndex] = useState(0);

  // Fetch pool detail using TanStack Query
  const { data: poolDataResponse, isLoading: loadingPool, error: poolError } = usePool(Number(poolId));
  const pool = poolDataResponse?.pool || null;
  const { data: notificationsData, isLoading: loadingNotifications } = useNotificationsByPoolId(Number(poolId));

  useMarkPoolNotificationsReadOnVisit(poolId);

  const poolNotifications = useMemo(() => {
    if (!notificationsData?.notifications) return [];
    return notificationsData.notifications;
  }, [notificationsData]);

  useEffect(() => {
    // Redirect to pools page if pool is not found or an error occurs
    let redirectTimeout: ReturnType<typeof setTimeout>;
    if (!loadingPool && (poolError || !pool)) {
      redirectTimeout = setTimeout(() => {
        navigate('/pools');
      }, 2000);
    }
    return () => clearTimeout(redirectTimeout);
  }, [loadingPool, poolError, pool, navigate]);

  // Filter counteroffers with status "sent"
  const availableCounterOffers = useMemo(() => {
    if (!pool) return [];
    return getAvailableCounterOffers(pool.offers);
  }, [pool]);

  // Reset index when pool changes or when counteroffers change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAvailableCounterOfferIndex(0);
  }, [poolId, availableCounterOffers.length]);

  const handlePreviousCounterOffer = () => {
    setAvailableCounterOfferIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextCounterOffer = () => {
    setAvailableCounterOfferIndex((prev) => Math.min(availableCounterOffers.length - 1, prev + 1));
  };

  const handleOffer = () => {
    setShowSendOfferModal(true);
  };

  const handleCounterOffer = () => {
    setShowSendOfferModal(false);
    setShowCounterOfferModal(true);
  };

  const actions = useMemo(() => {
    if (!pool) return null;

    const showingCounterOffer = availableCounterOffers[availableCounterOfferIndex];
    const showingOffer = showingCounterOffer || pool.offers?.[0];
    const offerId = showingOffer?.id;
    if (isBorrower(user)) {
      return (
        <PoolBorrowerActions
          pool={pool}
          onDelete={() => setConfirmAction({ type: 'closePool', isOpen: true })}
          onAcceptOffer={() => setConfirmAction({ type: 'acceptOffer', offerId, isOpen: true })}
          onRejectOffer={() => setConfirmAction({ type: 'rejectOffer', offerId, isOpen: true })}
          onAcceptCounterOffer={() => setConfirmAction({ type: 'acceptCounterOffer', offerId, isOpen: true })}
          onRejectCounterOffer={() => setConfirmAction({ type: 'rejectCounterOffer', offerId, isOpen: true })}
        />
      );
    }
    return (
      <PoolInvestorActions
        pool={pool}
        onOffer={handleOffer}
        onCounterOffer={handleCounterOffer}
        onWithdrawOffer={() => setConfirmAction({ type: 'withdrawOffer', offerId, isOpen: true })}
        onWithdrawCounterOffer={() => setConfirmAction({ type: 'withdrawCounterOffer', offerId, isOpen: true })}
      />
    );
  }, [user, pool, availableCounterOffers, availableCounterOfferIndex]);

  const closeConfirmModal = () => {
    setConfirmAction({ type: confirmAction.type, isOpen: false });
  };

  const handleConfirmSuccess = () => {
    if (confirmAction.type === 'closePool') {
      navigate('/pools');
    }
    // For other actions, the query invalidation will refresh the data automatically
  };

  const counterOfferInfo = useMemo(() => {
    return availableCounterOffers[availableCounterOfferIndex]?.counter_info || null;
  }, [availableCounterOfferIndex, availableCounterOffers]);

  const infoCardTitle = useMemo(() => {
    const tabLabel = activeTab === 'overview' ? 'Overview' : 'Assigned Documents';
    const counterOfferLabel =
      availableCounterOffers.length > 1 ? ` - Counter Offer #${availableCounterOfferIndex + 1}` : '';
    return `Pool #${poolId}${counterOfferLabel} - ${tabLabel}`;
  }, [poolId, activeTab, availableCounterOffers.length, availableCounterOfferIndex]);

  if (loadingPool) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div style={{ fontFamily: 'var(--ep-font-avenir)', color: '#767676' }}>Loading pool...</div>
      </div>
    );
  }

  if (poolError || !pool) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div style={{ fontFamily: 'var(--ep-font-avenir)', color: '#DC2626' }}>
          {poolError?.message || 'Failed to load pool'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24">
      <main className="w-full px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-4">
            <Breadcrumb poolId={poolId} />

            {/* Stepper wrapper for tabs and info card */}
            <div className="flex flex-col gap-4">
              {/* Tabs with inline Counteroffer Stepper */}
              <PoolDetailTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                pool={pool}
                counterOfferInfo={counterOfferInfo}
                totalCounterOffers={availableCounterOffers.length}
                activeCounterOfferIndex={availableCounterOfferIndex}
                onPreviousCounterOffer={handlePreviousCounterOffer}
                onNextCounterOffer={handleNextCounterOffer}
              />

              {/* Single InfoCard for both tabs */}
              <InfoCard
                title={infoCardTitle}
                statusBadge={
                  <PoolStatusPill
                    status={pool.status}
                    counterOffer={hasCounterOffer(pool.offers)}
                    isInvestor={isInvestor(user)}
                  />
                }
                actions={actions}
                isFavorite={!!pool.is_favorite}
                poolId={pool.id}
              >
                <SlideContentWrapper
                  currentIndex={availableCounterOfferIndex}
                  totalItems={availableCounterOffers.length}
                  onPrevious={handlePreviousCounterOffer}
                  onNext={handleNextCounterOffer}
                >
                  {activeTab === 'overview' ? (
                    <PoolOverviewContent
                      pool={pool}
                      isInvestor={isInvestor(user)}
                      counterOfferInfo={counterOfferInfo}
                    />
                  ) : (
                    <PoolDocumentsContent pool={pool} counterOfferInfo={counterOfferInfo} />
                  )}
                </SlideContentWrapper>
              </InfoCard>
            </div>

            <ActivitySection notifications={poolNotifications} loading={loadingNotifications} />
          </div>
        </div>
      </main>

      {pool && (
        <ConfirmActionModal
          pool={pool}
          counterOfferInfo={counterOfferInfo}
          offerId={confirmAction.offerId}
          isOpen={confirmAction.isOpen}
          onClose={closeConfirmModal}
          onSuccess={handleConfirmSuccess}
          actionType={confirmAction.type}
        />
      )}

      {pool && isInvestor(user) && (
        <>
          <SendOfferModal pool={pool} isOpen={showSendOfferModal} onClose={() => setShowSendOfferModal(false)} />
          <CounterOfferModal
            pool={pool}
            isOpen={showCounterOfferModal}
            onClose={() => setShowCounterOfferModal(false)}
          />
        </>
      )}
    </div>
  );
}
